import React, { useCallback, useEffect, useId, useState } from 'react';
import QuarterPicker from '../QuarterPicker';
import { apiService } from '../../services/api';
import { useConfirm } from '../../contexts/ConfirmContext';
import { useToast } from '../../contexts/ToastContext';
import * as fmt from '../../lib/format';
import { ImportRecord, ImportSummary } from '../plan/types';

// The body limit of the server is 100 KB; a planning sheet is about 20 KB.
const MAX_BYTES = 90_000;

const quarterNow = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${Math.floor(d.getMonth() / 3) + 1}`;
};

const BUTTON = 'inline-flex items-center justify-center rounded-lg px-3 py-2 text-sm font-medium transition-colors motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50';

function Summary({ s, written }: { s: ImportSummary; written: boolean }) {
  const line = (n: number, one: string, many: string) => `${fmt.count(n)} ${n === 1 ? one : many}`;
  return (
    <div className="space-y-3 text-sm text-gray-800" aria-live="polite">
      <p className="font-medium text-gray-900">
        {written ? 'Imported.' : 'Checked. Nothing has been changed yet.'} {line(s.rows, 'row', 'rows')} read, {line(s.keyed, 'with a Jira ticket', 'with a Jira ticket')}.
      </p>
      <ul className="list-disc space-y-1 pl-5">
        <li>{line(s.notes_filled, 'ticket gets its notes from the sheet', 'tickets get their notes from the sheet')} (only the cells the sheet fills; a note is never blanked).</li>
        <li>{line(s.new_ideas, 'new idea is added', 'new ideas are added')}; {line(s.already_known, 'was already known', 'were already known')}.</li>
      </ul>
      {s.keys_not_in_mirror.length > 0 && (
        <p>
          <span className="font-medium">Not in the portal yet:</span> {s.keys_not_in_mirror.join(', ')}. They arrive with the next sync; import again then.
        </p>
      )}
      {s.unmatched_projects.length > 0 && (
        <p>
          <span className="font-medium">Not a Jira project:</span> {s.unmatched_projects.join(', ')}. Their ideas are kept under the name as written.
        </p>
      )}
      {s.disagreements.length > 0 && (
        <div>
          <p className="font-medium">Where the sheet and Jira differ (Jira wins; nothing is changed in Jira):</p>
          <ul className="mt-1 list-disc space-y-0.5 pl-5">
            {s.disagreements.map((d) => (
              <li key={d.key}>
                <span className="font-medium">{d.key}</span>: the sheet says {d.sheet}, Jira says {d.jira}.
              </li>
            ))}
          </ul>
        </div>
      )}
      {s.warnings.length > 0 && (
        <ul className="list-disc space-y-0.5 pl-5 text-amber-800">
          {s.warnings.map((w) => (
            <li key={w}>{w}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Settings → Plan: bring the Google Sheet's planning rows into the portal. Check first, then import; importing the
// same file again adds nothing twice and never blanks a note.
export default function PlanImportTab() {
  const uid = useId();
  const confirm = useConfirm();
  const { showToast } = useToast();
  const [quarter, setQuarter] = useState(quarterNow());
  const [file, setFile] = useState<{ name: string; text: string } | null>(null);
  const [summary, setSummary] = useState<{ s: ImportSummary; written: boolean } | null>(null);
  const [busy, setBusy] = useState(false);
  const [imports, setImports] = useState<ImportRecord[]>([]);
  const quarterId = quarter.replace('-', '-Q');

  const loadImports = useCallback(async () => {
    try {
      const r = await apiService.getPlanImports();
      if (r.success) setImports(r.data);
    } catch {
      /* the list is a convenience: the import itself does not need it */
    }
  }, []);
  useEffect(() => {
    void loadImports();
  }, [loadImports]);

  const choose = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    setSummary(null);
    if (!f) return setFile(null);
    if (f.size > MAX_BYTES) {
      setFile(null);
      return showToast('error', 'That file is too big for a planning sheet. Export just the planning tab as CSV.');
    }
    setFile({ name: f.name, text: await f.text() });
  };

  const run = async (dryRun: boolean) => {
    if (!file) return;
    if (!dryRun) {
      const yes = await confirm({
        title: `Import ${summary ? fmt.count(summary.s.rows) : ''} rows?`.replace('  ', ' '),
        message: `Notes and new ideas go into the plan for ${fmt.quarter(quarter)}. Jira is not changed, and importing the same file again adds nothing twice.`,
        confirmLabel: 'Import sheet',
        tone: 'default',
      });
      if (!yes) return;
    }
    setBusy(true);
    try {
      const r = await apiService.importPlanSheet(file.text, file.name, quarterId, dryRun);
      if (!r.success) throw new Error(r.error);
      setSummary({ s: r.data.summary, written: !dryRun });
      if (!dryRun) {
        showToast('success', 'Sheet imported');
        void loadImports();
      }
    } catch (e: any) {
      showToast('error', e?.response?.data?.error || e?.message || 'Could not read the sheet');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Plan import</h3>
        <p className="text-sm text-gray-600">Bring the Google Sheet's planning rows into the Plan. The sheet can keep being used until everyone agrees to stop.</p>
      </div>

      <div className="space-y-4 rounded-2xl bg-white p-5 shadow-sm ring-1 ring-gray-200/70">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <span id={`${uid}-q`} className="block text-sm font-medium text-gray-800">Quarter the sheet is for</span>
            <div className="mt-1" aria-labelledby={`${uid}-q`}>
              <QuarterPicker value={quarter} onChange={(v) => { setQuarter(v); setSummary(null); }} className="" />
            </div>
          </div>
          <div className="min-w-[14rem] flex-1">
            <label htmlFor={`${uid}-file`} className="block text-sm font-medium text-gray-800">The sheet, saved as CSV</label>
            <input
              id={`${uid}-file`}
              type="file"
              accept=".csv,text/csv"
              onChange={choose}
              className="mt-1 block w-full text-sm text-gray-700 file:mr-3 file:rounded-lg file:border-0 file:bg-gray-100 file:px-3 file:py-2 file:text-sm file:font-medium file:text-gray-800 hover:file:bg-gray-200"
            />
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" disabled={!file || busy} onClick={() => run(true)} className={`${BUTTON} border border-gray-300 bg-white text-gray-800 hover:bg-gray-50`}>
            {busy ? 'Reading…' : 'Check first'}
          </button>
          <button type="button" disabled={!file || busy || !summary || summary.written} onClick={() => run(false)} className={`${BUTTON} bg-blue-700 text-white hover:bg-blue-800`}>
            Import sheet
          </button>
        </div>
        {summary && <Summary s={summary.s} written={summary.written} />}
      </div>

      {imports.length > 0 && (
        <section aria-label="Earlier imports">
          <h4 className="text-sm font-semibold text-gray-900">Earlier imports</h4>
          <ul className="mt-2 divide-y divide-gray-100 rounded-2xl bg-white text-sm shadow-sm ring-1 ring-gray-200/70">
            {imports.map((i) => (
              <li key={i.id} className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 px-4 py-3">
                <span className="min-w-0 break-words text-gray-900">{i.filename ?? 'A sheet'} for {fmt.quarter(i.quarter)}</span>
                <span className="text-gray-600">
                  {fmt.dateTime(i.imported_at)}{i.imported_by ? ` by ${i.imported_by}` : ''} · {fmt.count(i.summary.new_ideas)} new {i.summary.new_ideas === 1 ? 'idea' : 'ideas'}, {fmt.count(i.summary.notes_filled)} {i.summary.notes_filled === 1 ? 'ticket' : 'tickets'} filled
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
